import React, { useEffect, useState } from "react";
import moment from "moment";
import ReactPaginate from "react-paginate";
import LoadingModal from "../components/modals/LoadingModal";
import { notify } from "../components/actions/CommonActions";
import { GoStop } from "react-icons/go";
import {
  getBackOfficeReports,
  getDebitOnDemandReports,
} from "../services/index";

//  Constants

const LIMIT = 10;

const formatDate = (date) => date.toISOString().split("T")[0];

//  Helpers

const deriveColumns = (data) => {
  if (!data || data.length === 0) return [];
  return Object.keys(data[0]).filter((key) => key !== "summary");
};

const formatColumnLabel = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const formatCell = (key, value) => {
  if (value === null || value === undefined) return "—";
  const k = key.toLowerCase();

  if (
    typeof value === "string" &&
    (k.includes("date") || k.includes("createdat") || k.includes("updatedat"))
  ) {
    const m = moment(value);
    return m.isValid() ? m.format("DD MMM, YYYY HH:mm") : value;
  }

  if (
    k.includes("amount") &&
    (typeof value === "number" || (typeof value === "string" && !isNaN(value)))
  ) {
    return Number(value).toLocaleString();
  }

  return String(value);
};

const buildSearchParams = (page, filters) => {
  const params = new URLSearchParams();
  if (filters.status) params.append("status", filters.status);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  params.append("page", page);
  params.append("limit", LIMIT);
  return params.toString();
};

//  Sub-components 

const EmptyState = ({ message = "No data available." }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "28vh",
      flexDirection: "column",
    }}
  >
    <GoStop color="#007dcc" size={40} />
    <p style={{ fontSize: "17px", marginTop: "10px", color: "#555" }}>
      {message}
    </p>
  </div>
);

const DataTable = ({
  data,
  columns,
  pageCount,
  currentPage,
  onPageChange,
  emptyMessage,
}) => {
  if (!data || data.length === 0) return <EmptyState message={emptyMessage} />;

  return (
    <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
      <table className="fl-table">
        <thead>
          <tr style={{ borderBottom: "0.5px solid #f1f1f1" }}>
            {columns.map((col) => (
              <th key={col}>{formatColumnLabel(col)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr className="tr" key={row?.id ?? idx}>
              {columns.map((col) => (
                <td className="gray-td" key={col}>
                  {typeof row[col] === "string" &&
                  (row[col].startsWith("http://") ||
                    row[col].startsWith("https://")) ? (
                    <a
                      href={row[col]}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#007dcc", wordBreak: "break-all" }}
                    >
                      {row[col]}
                    </a>
                  ) : (
                    formatCell(col, row[col])
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <ReactPaginate
        previousLabel={"Prev"}
        nextLabel={"Next"}
        breakLabel={"..."}
        breakClassName={"break-me"}
        pageCount={pageCount}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5}
        onPageChange={onPageChange}
        containerClassName={"pagination"}
        subContainerClassName={"pages pagination"}
        activeClassName={"active"}
        forcePage={currentPage - 1}
      />
    </div>
  );
};

//  Main Component 

const PremiumTransactionHistory = () => {
  //  Tab State
  const [activeTab, setActiveTab] = useState("backOffice");

  //  Filter State
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [status, setStatus] = useState("");

  //  Back Office Table State
  const [backOfficeData, setBackOfficeData] = useState([]);
  const [backOfficeColumns, setBackOfficeColumns] = useState([]);
  const [backOfficePageCount, setBackOfficePageCount] = useState(0);
  const [backOfficePage, setBackOfficePage] = useState(1);

  //  Debit On Demand Table State
  const [debitData, setDebitData] = useState([]);
  const [debitColumns, setDebitColumns] = useState([]);
  const [debitPageCount, setDebitPageCount] = useState(0);
  const [debitPage, setDebitPage] = useState(1);

  //  Loading State
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  //  Data Fetching 

  const fetchBackOffice = async (page = 1, filters) => {
    try {
      const res = await getBackOfficeReports(buildSearchParams(page, filters));
      const rows = res?.data?.data ?? res?.data ?? [];
      const total = res?.data?.totalPages ?? res?.totalPages ?? 0;
      setBackOfficeData(rows);
      setBackOfficeColumns(deriveColumns(rows));
      setBackOfficePageCount(total);
    } catch (err) {
      console.error("Back office fetch error:", err);
      notify("danger", "Error", "Failed to load back office transactions.");
    }
  };

  const fetchDebitOnDemand = async (page = 1, filters) => {
    try {
      const res = await getDebitOnDemandReports(
        buildSearchParams(page, filters),
      );
      const rows = res?.data?.data ?? res?.data ?? [];
      const total = res?.data?.totalPages ?? res?.totalPages ?? 0;
      setDebitData(rows);
      setDebitColumns(deriveColumns(rows));
      setDebitPageCount(total);
    } catch (err) {
      console.error("Debit on demand fetch error:", err);
      notify("danger", "Error", "Failed to load debit on demand transactions.");
    }
  };

  //  Effects 

  useEffect(() => {
    const filters = { status, startDate, endDate };
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchBackOffice(1, filters),
        fetchDebitOnDemand(1, filters),
      ]);
      setLoading(false);
    };
    init();
  }, []);

  //  Handlers

  const handleSearch = async (e) => {
    e.preventDefault();
    if (startDate > endDate) {
      notify("danger", "Invalid Date", "Start date cannot be after end date.");
      return;
    }
    const filters = { status, startDate, endDate };
    setIsLoading(true);
    setBackOfficePage(1);
    setDebitPage(1);
    await Promise.all([
      fetchBackOffice(1, filters),
      fetchDebitOnDemand(1, filters),
    ]);
    setIsLoading(false);
  };

  const handleBackOfficePageClick = async ({ selected }) => {
    const page = selected + 1;
    const filters = { status, startDate, endDate };
    setBackOfficePage(page);
    setIsLoading(true);
    await fetchBackOffice(page, filters);
    setIsLoading(false);
  };

  const handleDebitPageClick = async ({ selected }) => {
    const page = selected + 1;
    const filters = { status, startDate, endDate };
    setDebitPage(page);
    setIsLoading(true);
    await fetchDebitOnDemand(page, filters);
    setIsLoading(false);
  };

  //  Styles 
  const tabStyle = (tab) => ({
    padding: "9px 22px",
    cursor: "pointer",
    border: "none",
    borderBottom:
      activeTab === tab ? "2px solid #007dcc" : "2px solid transparent",
    background: "none",
    fontWeight: activeTab === tab ? 700 : 400,
    color: activeTab === tab ? "#007dcc" : "#555",
    fontSize: "14px",
    transition: "all 0.2s",
  });

  //  Loading Screen 

  if (loading) {
    return (
      <div className="typewriter-container">
        <div className="typewriter">
          <div className="slide">
            <i></i>
          </div>
          <div className="paper"></div>
          <div className="keyboard"></div>
        </div>
        <code style={{ color: "#000" }}>Getting your app ready...</code>
      </div>
    );
  }

  //  Render 

  return (
    <div style={{ overflowX: "auto" }}>
      {/* Filters */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          margin: "10px 0 16px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <div>
            <p style={{ fontSize: "14px", marginBottom: "5px" }}>Start date</p>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                border: "1px solid black",
                borderRadius: "5px",
                padding: "5px 10px",
                outline: "none",
              }}
            />
          </div>
          <div>
            <p style={{ fontSize: "14px", marginBottom: "5px" }}>End date</p>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                border: "1px solid black",
                borderRadius: "5px",
                padding: "5px 10px",
                outline: "none",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <form
            onSubmit={handleSearch}
            style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}
          >
            {/* Status filter — only visible on Debit On Demand tab */}
            {activeTab === "debit" && (
              <div>
                <p style={{ fontSize: "14px", marginBottom: "5px" }}>Status</p>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{
                    border: "1px solid black",
                    borderRadius: "5px",
                    padding: "5px 10px",
                    outline: "none",
                  }}
                >
                  <option value="">All</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            )}
            <button
              type="submit"
              style={{
                border: "none",
                backgroundColor: "#007dcc",
                color: "white",
                padding: "4px 14px",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid #e0e0e0", marginBottom: "16px" }}>
        <button
          style={tabStyle("backOffice")}
          onClick={() => setActiveTab("backOffice")}
        >
          Back Office Transactions
        </button>
        <button style={tabStyle("debit")} onClick={() => setActiveTab("debit")}>
          Debit On Demand
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "backOffice" && (
        <DataTable
          data={backOfficeData}
          columns={backOfficeColumns}
          pageCount={backOfficePageCount}
          currentPage={backOfficePage}
          onPageChange={handleBackOfficePageClick}
          emptyMessage="No back office transactions found."
        />
      )}

      {activeTab === "debit" && (
        <DataTable
          data={debitData}
          columns={debitColumns}
          pageCount={debitPageCount}
          currentPage={debitPage}
          onPageChange={handleDebitPageClick}
          emptyMessage="No debit on demand transactions found."
        />
      )}

      <LoadingModal show={isLoading} />
    </div>
  );
};

export default PremiumTransactionHistory;
